import React, { HTMLAttributes } from 'react'

interface SwitchProperties extends HTMLAttributes<HTMLInputElement> {
  id: string
  label?: string
  'data-on'?: string
  isChecked?: boolean
  'data-off'?: string
  description?: string
  activeColor?: 'blue' | 'green' | 'purple' | 'orange' | 'pink' | 'red'
  inactiveColor?: string
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: {
    containerWidth: 32,
    containerHeight: 20,
    knobWidth: 16,
    knobHeight: 16,
    text: 'text-sm',
  },
  md: {
    containerWidth: 40,
    containerHeight: 24,
    knobWidth: 20,
    knobHeight: 20,
    text: 'text-base',
  },
  lg: {
    containerWidth: 48,
    containerHeight: 28,
    knobWidth: 24,
    knobHeight: 24,
    text: 'text-lg',
  },
}
const ToggleSwitch: React.FC<SwitchProperties> = (props) => {
  const {
    onChange: changeHandler,
    id,
    isChecked,
    inactiveColor = '#EEEEEE',
    activeColor = 'blue',
    className,
    disabled,
    size = 'md',
    ...rest
  } = props

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeHandler && changeHandler(event)
  }

  const labelId = `label-${id}`
  const descriptionId = `description-${id}`
  const labelBy = `${labelId} ${descriptionId}`

  const { containerWidth, knobWidth } = sizeClasses[size]
  const offTranslate = 2 // Adjust as needed for slight offset
  const onTranslate = containerWidth - knobWidth - offTranslate

  return (
    <label
      htmlFor={id}
      className={`flex items-start gap-5 ${sizeClasses[size].text}`}
    >
      <div
        style={{
          width: sizeClasses[size].containerWidth,
          height: sizeClasses[size].containerHeight,
          backgroundColor: isChecked
            ? `var(--${activeColor}-100)`
            : `${inactiveColor}`,
        }}
        className={`relative inline-flex items-center cursor-pointer w-[${
          sizeClasses[size].containerWidth
        }px] h-[${
          sizeClasses[size].containerHeight
        }px] rounded-full transition-colors duration-200 ${
          isChecked
            ? `!bg-[var(--${activeColor}-100)]`
            : `!bg-[${inactiveColor}]`
        }`}
      >
        <input
          type='checkbox'
          id={id}
          className='opacity-0 w-0 h-0'
          checked={isChecked}
          onChange={onChange}
          aria-checked={isChecked}
          aria-labelledby={labelBy}
          disabled={disabled}
          {...rest}
        />
        <span
          className={`absolute cursor-pointer w-[${sizeClasses[size].knobWidth}px] h-[${sizeClasses[size].knobHeight}px] rounded-full bg-white shadow-md transition-transform duration-200`}
          style={{
            transform: `translateX(${
              isChecked ? onTranslate : offTranslate
            }px)`,
          }}
        ></span>
      </div>
      <div className='flex flex-col gap-1 ml-1'>
        <span id={labelId} className='cursor-pointer select-none leading-[1.4]'>
          {props.label}
        </span>
        {props.description && (
          <p
            className='text-sm tracking-[0.4px] leading-[1.3] text-gray-500'
            id={descriptionId}
          >
            {props.description}
          </p>
        )}
      </div>
    </label>
  )
}

export default ToggleSwitch
